import React from 'react';
import { Alert, Box, Chip, Stack, Typography } from '@mui/material';
import {
  Area,
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { useChartTheme } from '@modules/ui-components/hooks/useChartTheme';
import type { CreativeFatigueCurve, CreativeFatigueCurvePoint } from '../../creativeDecisions';
import { CREATIVE_DECISIONS_TEST_IDS } from '../../testIds';

interface ChartPoint {
  label: string;
  date: string;
  relative?: number;
  band?: [number, number];
  grossPerThousandMajor?: number;
  source: CreativeFatigueCurvePoint;
}

/**
 * Fatigue R2.6b — per-creative fatigue curve: relative unique-CTR over the
 * creative's lifetime (own d1-3 baseline = 1.0) with a volume-aware band,
 * the Tier-B change-point marker, Tier-A early-watch markers, and the
 * contextual-controls strip («это не креатив, это доставка/рынок»
 * discriminators). Watch-only evidence, never a verdict input (D-F5).
 */
export function CreativeFatigueCurveSection({
  curve,
  currency,
  currencyExponent,
}: {
  curve?: CreativeFatigueCurve;
  currency?: string;
  currencyExponent?: number;
}): React.ReactElement | null {
  const chart = useChartTheme();
  if (!curve) return null;

  if (curve.state === 'unavailable') {
    return (
      <Box
        sx={{ mt: 1.5, p: 1.5, border: 1, borderColor: 'divider', borderRadius: 1 }}
        data-testid={CREATIVE_DECISIONS_TEST_IDS.drawerFatigueCurve}
      >
        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Creative fatigue curve</Typography>
        <Alert severity="info" sx={{ mt: 1 }} data-testid={CREATIVE_DECISIONS_TEST_IDS.drawerFatigueCurveState}>
          {unavailableCopy(curve.reasonCodes)}
        </Alert>
      </Box>
    );
  }

  const exponent = currencyExponent ?? 2;
  const points: ChartPoint[] = curve.points.map((point) => ({
    label: point.date.slice(5),
    date: point.date,
    ...(point.relativeUniqueCtr !== undefined ? { relative: point.relativeUniqueCtr } : {}),
    ...(point.relativeLower !== undefined && point.relativeUpper !== undefined
      ? { band: [point.relativeLower, point.relativeUpper] as [number, number] }
      : {}),
    ...(point.grossPerThousandImpressionsMinor !== undefined
      ? { grossPerThousandMajor: point.grossPerThousandImpressionsMinor / 10 ** exponent }
      : {}),
    source: point,
  }));
  const deviatingDays = curve.controls.filter((control) => control.seasonalityDeviates);
  const accessibleSummary = [
    `Creative fatigue curve: rolling 3-day unique CTR relative to the creative's own first-days baseline.`,
    `${points.length} day points.`,
    curve.changePointAt ? `Confirmed change point at ${curve.changePointAt}.` : 'No confirmed change point.',
    curve.earlyWatchReasons?.length ? `Early watch signals: ${curve.earlyWatchReasons.join(', ')}.` : '',
  ].filter(Boolean).join(' ');

  return (
    <Box
      sx={{ mt: 1.5, p: 1.5, border: 1, borderColor: 'divider', borderRadius: 1 }}
      data-testid={CREATIVE_DECISIONS_TEST_IDS.drawerFatigueCurve}
    >
      <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={1}>
        <Box>
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Creative fatigue curve</Typography>
          <Typography variant="caption" color="text.secondary">
            {`Rolling 3-day unique CTR vs own baseline (1.0) · realized gross per 1000 impressions (audit) · target refresh cycle ${curve.targetRefreshCycleDays}d`}
          </Typography>
        </Box>
        <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap">
          <Chip
            size="small"
            variant="outlined"
            color={curve.state === 'available' ? 'success' : 'warning'}
            label={curve.state}
            data-testid={CREATIVE_DECISIONS_TEST_IDS.drawerFatigueCurveState}
          />
          {curve.earlyWatchReasons?.map((reason) => (
            <Chip
              key={reason}
              size="small"
              color="warning"
              variant="outlined"
              label={`early watch · ${reason.replaceAll('_', ' ')}`}
              data-testid={CREATIVE_DECISIONS_TEST_IDS.drawerFatigueCurveEarlyWatch}
            />
          ))}
        </Stack>
      </Stack>

      {curve.state === 'partial' && (
        <Alert severity="warning" sx={{ mt: 1 }}>
          {`The curve is partial: ${humanReasons(curve.reasonCodes)}.`}
        </Alert>
      )}
      {deviatingDays.length > 0 && (
        <Alert severity="info" sx={{ mt: 1 }} data-testid={CREATIVE_DECISIONS_TEST_IDS.drawerFatigueCurveSeasonality}>
          {`Account-level volume deviated more than 2x from its trailing mean on ${deviatingDays.length} day${deviatingDays.length === 1 ? '' : 's'} — market/delivery co-movement, not creative wear, may explain moves there.`}
        </Alert>
      )}

      <Box role="img" aria-label={accessibleSummary} sx={{ width: '100%', height: { xs: 260, sm: 300 }, mt: 1.25 }}>
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={points} margin={{ top: 8, right: 8, bottom: 0, left: 4 }}>
            <CartesianGrid strokeDasharray="3 3" stroke={chart.grid} />
            <XAxis dataKey="label" tick={chart.axisTick} />
            <YAxis
              yAxisId="relative"
              tick={chart.axisTick}
              domain={[0, 'auto']}
              tickFormatter={(value: number) => value.toFixed(2)}
            />
            <YAxis
              yAxisId="gross"
              orientation="right"
              tick={chart.axisTick}
              tickFormatter={(value: number) => compactMoney(value, currency)}
            />
            <RechartsTooltip
              formatter={(value, name) => {
                if (typeof value !== 'number') return ['n/a', String(name)];
                if (name === 'Gross / 1000 imp (audit)') return [compactMoney(value, currency), String(name)];
                if (Array.isArray(value)) return [String(value), String(name)];
                return [value.toFixed(3), String(name)];
              }}
            />
            <Legend />
            <Area
              yAxisId="relative"
              dataKey="band"
              name="Volume-aware band"
              stroke="none"
              fill={chart.info}
              fillOpacity={0.15}
              connectNulls={false}
            />
            <Line
              yAxisId="relative"
              type="monotone"
              dataKey="relative"
              name="Relative unique CTR"
              stroke={chart.primary}
              strokeWidth={2.5}
              connectNulls={false}
            />
            <Line
              yAxisId="gross"
              type="monotone"
              dataKey="grossPerThousandMajor"
              name="Gross / 1000 imp (audit)"
              stroke={chart.positive}
              strokeDasharray="5 4"
              connectNulls={false}
            />
            <ReferenceLine yAxisId="relative" y={1} stroke={chart.grid} strokeDasharray="2 4" />
            {curve.changePointAt && (
              <ReferenceLine
                yAxisId="relative"
                x={curve.changePointAt.slice(5, 10)}
                stroke={chart.negative}
                strokeWidth={2}
                label={{ value: 'change point', fill: chart.negative, fontSize: 11, position: 'top' }}
              />
            )}
            {points[curve.targetRefreshCycleDays - 1] && (
              <ReferenceLine
                yAxisId="relative"
                x={points[curve.targetRefreshCycleDays - 1].label}
                stroke={chart.warning}
                strokeDasharray="4 4"
                label={{ value: `refresh target ${curve.targetRefreshCycleDays}d`, fill: chart.warning, fontSize: 11, position: 'insideTopRight' }}
              />
            )}
          </ComposedChart>
        </ResponsiveContainer>
      </Box>

      <Stack component="ol" role="list" spacing={0.5} sx={{ m: 0, mt: 1.25, p: 0, listStyle: 'none' }}>
        {points.map((point) => (
          <Box
            component="li"
            key={point.date}
            data-testid={CREATIVE_DECISIONS_TEST_IDS.drawerFatigueCurveDay(point.date)}
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: '84px repeat(2, minmax(0, 1fr))' },
              gap: 0.75,
              py: 0.5,
              borderBottom: 1,
              borderColor: 'divider',
            }}
          >
            <Typography variant="caption" sx={{ fontWeight: 700 }}>{point.date}</Typography>
            <Typography variant="caption">
              {point.relative !== undefined
                ? `Relative uCTR ${point.relative.toFixed(3)} (reach ${point.source.rollingReach.toLocaleString()})`
                : `uCTR ${point.source.state.replaceAll('_', ' ')}`}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {point.grossPerThousandMajor !== undefined
                ? `Gross/1000 imp ${compactMoney(point.grossPerThousandMajor, currency)}`
                : 'gross evidence pending'}
            </Typography>
          </Box>
        ))}
      </Stack>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
        Watch-only contextual evidence for the human refresh decision — it never enters kill/scale verdicts.
        Gross is audit context, not a decision basis. Aggregate decay views understate true decay (survivors only).
      </Typography>
    </Box>
  );
}

function unavailableCopy(reasonCodes: readonly string[]): string {
  if (reasonCodes.includes('insufficient_volume')) {
    return 'This creative is below the powered-volume floor (40k lifetime impressions) — its daily click rates are statistically unmeasurable, so no curve is drawn. Powered creatives carry ~94% of account impressions.';
  }
  if (reasonCodes.includes('window_below_minimum')) {
    return 'Fewer than 4 complete delivery days observed — the rolling-window curve needs a longer run before it can say anything honest.';
  }
  return `The fatigue curve is unavailable: ${humanReasons(reasonCodes)}.`;
}

function humanReasons(reasonCodes: readonly string[]): string {
  return reasonCodes.length ? reasonCodes.map((reason) => reason.replaceAll('_', ' ')).join(' · ') : 'no registered reason';
}

function compactMoney(value: number, currency?: string): string {
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: currency ?? 'USD',
      notation: 'compact',
      maximumFractionDigits: 2,
    }).format(value);
  } catch {
    return `${currency ?? ''} ${value.toFixed(2)}`;
  }
}
